import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Modal from 'react-modal';
import { ActionLink } from 'components/shared';
import { formatURLFacebookShare, formatURLMailto } from 'helpers/strings';
import { Button } from '@companycam/slab-web';
import CCClipboard from 'components/shared/CCClipboard';

const URL_LARGE = 'url_large';
const URL_MEDIUM = 'url_medium';
const URL_SMALL = 'url_small';

const SingleShareModal = ({ isOpen, photo, onRequestClose }) => {
  const [copied, setCopied] = useState(false);
  const [version, setVersion] = useState(URL_LARGE);

  useEffect(() => {
    if (!isOpen) {
      setCopied(false);
      setVersion(URL_LARGE);
    }
  }, [isOpen]);

  const onSuccess = () => {
    setCopied(true);
  };

  if (!photo) {
    return null;
  }

  const url = photo[version];

  return (
    <Modal
      isOpen={isOpen}
      ariaHideApp
      onRequestClose={onRequestClose}
      contentLabel="Share a photo"
      overlayClassName="modal"
      className="modal-content-wrap"
    >
      <ActionLink className="modal-close-button" onClick={onRequestClose}>
        <i className="mdi mdi-close" />
      </ActionLink>
      <h3 className="modal-title">
        <FormattedMessage
          id="Photos.singleShareModal.sharePhoto"
          defaultMessage="Share Photo"
          description="section header"
        />
      </h3>
      <form>
        <div className="instruction-label">
          <FormattedMessage
            id="Photos.singleShareModal.instruction"
            defaultMessage="Which version would you like to share?"
            description="message to the user asking them to select a version to share"
          />
        </div>

        <div className="url-options">
          <label>
            <input
              type="radio"
              checked={version === URL_SMALL}
              onChange={() => {
                setVersion(URL_SMALL);
                setCopied(false);
              }}
            />
            <FormattedMessage
              id="Photos.singleShareModal.small"
              defaultMessage="Small"
              description="label to the user describing the adjacent option"
            />
          </label>
          <label>
            <input
              type="radio"
              checked={version === URL_MEDIUM}
              onChange={() => {
                setVersion(URL_MEDIUM);
                setCopied(false);
              }}
            />
            <FormattedMessage
              id="Photos.singleShareModal.medium"
              defaultMessage="Medium"
              description="label to the user describing the adjacent option"
            />
          </label>
          <label>
            <input
              type="radio"
              checked={version === URL_LARGE}
              onChange={() => {
                setVersion(URL_LARGE);
                setCopied(false);
              }}
            />
            <FormattedMessage
              id="Photos.singleShareModal.large"
              defaultMessage="Large"
              description="label to the user describing the adjacent option"
            />
          </label>
        </div>

        <div className="link-copy-box-container">
          <div className="link-copy-box">{url}</div>
          <CCClipboard copy={url} onCopy={onSuccess}>
            <i className="mdi mdi-link-variant" />
            {copied ? (
              <FormattedMessage id="Common.share.copied!" />
            ) : (
              <FormattedMessage id="Common.share.copyLink" />
            )}
          </CCClipboard>
        </div>
        <div className="btn-container full">
          <Button
            color="primary"
            icon={{
              name: "open-in-new",
              position: "before",
            }}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FormattedMessage
              id="Photos.singleShareModal.viewPhoto"
              defaultMessage="View Photo"
              description="Button that allows the user to open the individual photo in a new tab"
            />
          </Button>
          <Button
            icon={{
              name: "facebook",
              position: "before",
            }}
            href={formatURLFacebookShare({
              url,
              title: 'Photo Taken Using CompanyCam',
            })}
            target="_blank"
            rel="noopener noreferrer"
          >
            Facebook
          </Button>
          <Button
            icon={{
              name: "email-outline",
              position: "before",
            }}
            href={formatURLMailto({
              subject: 'Check out my CompanyCam photo',
              bodyLines: [
                'Here’s the photo I captured using CompanyCam.',
                '',
                url,
              ],
            })}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FormattedMessage id="Common.share.email" />
          </Button>
        </div>
      </form>
    </Modal>
  );
};

SingleShareModal.propTypes = {
  isOpen: PropTypes.bool,
  photo: PropTypes.object,
  onRequestClose: PropTypes.func,
};

export default SingleShareModal;
